<template>
  <!--
    Sticky banner that replaces the old `/network_error` full-page
    redirect. Listens for `app:backend-unavailable` and
    `app:backend-available` events dispatched by ApiClient.ts, so
    it covers every 503 / Network Error from any call in the app
    — without kicking the user out of whatever they were doing.

    UX contract:
    - Auto-hides as soon as any subsequent request returns 2xx
      (handleResponse emits `app:backend-available`).
    - The user can dismiss manually; dismissal is session-scoped,
      if backend goes down again later the banner re-appears.
  -->
  <Transition
    enter-active-class="transition-transform duration-200"
    enter-from-class="-translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="transition-transform duration-200"
    leave-from-class="translate-y-0"
    leave-to-class="-translate-y-full"
  >
    <div
      v-if="visible"
      class="backend-banner"
      role="status"
      aria-live="polite"
    >
      <div class="backend-banner__inner">
        <q-spinner-dots v-if="retrying" color="white" size="18px" />
        <q-icon v-else name="mdi-alert-circle" size="20px" />
        <span class="backend-banner__text">
          {{ $t('backend_unavailable_banner') || 'Backend temporarily unavailable — reconnecting…' }}
        </span>
        <q-btn
          flat
          dense
          size="sm"
          color="white"
          :label="$t('dismiss') || 'Dismiss'"
          @click="dismiss"
        />
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';

export default defineComponent({
  name: 'BackendUnavailableBanner',
  setup() {
    const visible = ref(false);
    const retrying = ref(true);
    const GRACE_MS = 10000;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let dismissedUntilNext = false;

    const onUnavailable = () => {
      if (dismissedUntilNext && visible.value) return;
      if (visible.value) {
        // Already visible — just refresh the "still retrying" hint.
        retrying.value = true;
        return;
      }
      if (graceTimer) return; // already queued
      graceTimer = setTimeout(() => {
        visible.value = true;
        retrying.value = true;
        graceTimer = null;
      }, GRACE_MS);
    };

    const onAvailable = () => {
      // Any 2xx means recovery — hide immediately.
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      if (visible.value) {
        visible.value = false;
        dismissedUntilNext = false;
      }
    };

    const dismiss = () => {
      visible.value = false;
      // Stays dismissed until we see at least one `available` event,
      // after which the next `unavailable` re-arms. Prevents the
      // banner from nagging a user who has already acknowledged an
      // outage, but still surfaces a NEW outage later in the session.
      dismissedUntilNext = true;
    };

    onMounted(() => {
      window.addEventListener('app:backend-unavailable', onUnavailable);
      window.addEventListener('app:backend-available', onAvailable);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('app:backend-unavailable', onUnavailable);
      window.removeEventListener('app:backend-available', onAvailable);
      if (graceTimer) clearTimeout(graceTimer);
    });

    return { visible, retrying, dismiss };
  },
});
</script>

<style scoped>
.backend-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: var(--warning, #f59e0b);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.backend-banner__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
}
.backend-banner__text {
  flex: 1;
}
</style>
